import { memo, useEffect } from 'react';
import { Field, WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import {
  FieldWrapper,
  FullSpanSubRowTD,
  SubRowLabel,
  SubRowLabelTD,
  TableSubRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import { REQUIRED_PROPERTIES_OPTIONS } from 'dashboard/forms/FormVerificationTemplateChecks/ExtractedPropertiesConfigurationElement';
import { TableActionTD } from 'dashboard/layouts/TableLayout';
import { RequiredPropertyConfig } from 'dashboard/models/verification-check-config/id-extracted-properties-detection';
import { indexForOverride } from 'dashboard/models/verification-check-config/utils';
import { CountryCodeLower } from 'dashboard/models/verification-template-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useToggle from 'lib/hooks/useToggle';

const TableSubRowActionTD = styled(TableActionTD)`
  border-bottom: none;
`;

interface OverrideExtractionRowProps {
  baseName: string;
  label: string;
  countryCode: CountryCodeLower;
  idClass: GovernmentIdClass;
  disabled: boolean;
}

/**
 * required properties override by country code and idclass
 * reference: https://www.notion.so/withpersona/required_properties-14447672d5144c3c9ce78912ca83355a
 */
const OverrideExtractionRow = memo(
  ({
    baseName,
    label,
    countryCode,
    idClass,
    disabled,
    input,
  }: OverrideExtractionRowProps & WrappedFieldProps) => {
    const extractionConfigs = input.value as RequiredPropertyConfig[];

    const overrideIndex = indexForOverride({
      attributesField: extractionConfigs,
      countryCode,
      idClass,
    });
    const name = `${baseName}.${overrideIndex}.properties`;
    const [overrideAttribute, { toggle: toggleOverride, set: setOverrideState }] = useToggle(
      overrideIndex !== -1,
    );

    useEffect(() => {
      setOverrideState(overrideIndex !== -1);
    }, [overrideIndex, setOverrideState]);

    const handleToggle = (enabled: boolean) => {
      if (enabled) {
        const overrideValues: RequiredPropertyConfig = {
          'country-code': countryCode.toUpperCase(),
          'id-class': idClass,
          properties: [],
        };
        input.onChange(extractionConfigs.concat(overrideValues));
      } else {
        const updatedConfig = [
          ...extractionConfigs.slice(0, overrideIndex),
          ...extractionConfigs.slice(overrideIndex + 1),
        ];
        input.onChange(updatedConfig);
      }

      toggleOverride();
    };

    return (
      <>
        <TableSubRow>
          <SubRowLabelTD>
            <Paragraph>{label}</Paragraph>
          </SubRowLabelTD>
          <TableSubRowActionTD>
            <ToggleSlider
              id='test'
              key={`toggle-${name}`}
              labelOn='On'
              labelOff='Off'
              value={overrideAttribute}
              onChange={handleToggle}
              disabled={disabled}
              data-test='override-toggle-slider__required-properties'
            />
          </TableSubRowActionTD>
        </TableSubRow>
        <TableSubRow>
          <FullSpanSubRowTD>
            <Spacer height={1} />
            {overrideAttribute && (
              <FieldWrapper>
                <Field
                  id={name}
                  key={name}
                  name={name}
                  component={ReactSelectField}
                  label={<SubRowLabel>Required attributes</SubRowLabel>}
                  data-test='country-configurations-override-table-row__required-properties'
                  options={REQUIRED_PROPERTIES_OPTIONS}
                  disabled={disabled}
                  isMulti
                />
              </FieldWrapper>
            )}
          </FullSpanSubRowTD>
        </TableSubRow>
      </>
    );
  },
);

OverrideExtractionRow.displayName = 'OverrideExtractionRow';
export default OverrideExtractionRow;
